import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Newspaper, ExternalLink, Filter } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NavBar } from '@/components/NavBar';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';

const SEVERITY_COLORS = { high: 'bg-[#FF3B30]', medium: 'bg-[#FF9500]', low: 'bg-[#2E7D32]', critical: 'bg-[#8B0000]' };
const SOURCE_COLORS = { GDELT: '#0097A7', GNews: '#5856D6', GDACS: '#FF3B30', Alert: '#FF9500' };

const SignalsPage = () => {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    api.getSignalsFeed(100)
      .then(res => setFeed(res.data?.feed || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = feed.filter(s => {
    if (sourceFilter !== 'all' && s.source !== sourceFilter) return false;
    if (severityFilter !== 'all' && s.severity !== severityFilter) return false;
    return true;
  });

  const sources = [...new Set(feed.map(s => s.source))];

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />
      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="signals-title">News & Signals</h1>
            <p className="text-[#4E6974] mt-2">Consolidated live feed from GDELT, GNews, and GDACS data sources across APAC.</p>
          </motion.div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            <div className="flex items-center gap-1 mr-4">
              <Filter className="h-4 w-4 text-[#4E6974]" />
              <span className="text-xs text-[#4E6974] uppercase tracking-wide">Source:</span>
            </div>
            {['all', ...sources].map(s => (
              <Button key={s} size="sm" variant={sourceFilter === s ? 'default' : 'outline'}
                className={sourceFilter === s ? 'bg-[#0D3B4F] hover:bg-[#0D3B4F]/90 text-xs' : 'text-xs'}
                onClick={() => setSourceFilter(s)} data-testid={`filter-source-${s}`}>
                {s === 'all' ? 'All sources' : s}
              </Button>
            ))}
            <div className="w-px bg-[#E0E0E0] mx-2" />
            {['all', 'high', 'medium', 'low'].map(s => (
              <Button key={s} size="sm" variant={severityFilter === s ? 'default' : 'outline'}
                className={severityFilter === s ? 'bg-[#0D3B4F] hover:bg-[#0D3B4F]/90 text-xs' : 'text-xs'}
                onClick={() => setSeverityFilter(s)} data-testid={`filter-severity-${s}`}>
                {s === 'all' ? 'All severity' : s.charAt(0).toUpperCase() + s.slice(1)}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20 text-[#4E6974]">Loading signals...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Newspaper className="h-12 w-12 text-[#4E6974] mx-auto mb-4" />
              <p className="text-[#4E6974]">No signals match your filters.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="signals-feed">
              {filtered.map((s, i) => (
                <motion.div key={s.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                  <Card className="bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.04)] border-none hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <Badge className={`${SEVERITY_COLORS[s.severity] || 'bg-[#4E6974]'} text-white text-[9px] uppercase`}>{s.severity}</Badge>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: SOURCE_COLORS[s.source] || '#4E6974' }}>{s.source}</span>
                            {s.country_name && <span className="text-[10px] text-[#4E6974]">{s.country_name}</span>}
                            {s.published_at && <span className="text-[10px] text-[#B0BEC5]">{formatDate(s.published_at)}</span>}
                          </div>
                          <h3 className="text-sm font-semibold text-[#0D3B4F] mb-1">{s.title}</h3>
                          {s.description && <p className="text-xs text-[#4E6974] line-clamp-2">{s.description}</p>}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {s.country_code && <Link to={`/countries/${s.country_code}`} className="text-[10px] text-[#0097A7] hover:underline">{s.country_code}</Link>}
                          {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#B0BEC5] hover:text-[#0097A7]"><ExternalLink className="h-3.5 w-3.5" /></a>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SignalsPage;
